import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Users, TrendingUp, DollarSign, Clock, Search, Sparkles, Trash2, Plus, Star, X, Copy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

const KPI = ({ label, value, sub, icon: Icon, dark, testId }) => (
  <div data-testid={testId} className={`${dark ? "bg-[#1C1917] text-white" : "bg-white border border-stone-200"} rounded-2xl p-6`}>
    <div className="flex items-start justify-between mb-4">
      <Icon className={`w-5 h-5 ${dark ? "text-[#D4A373]" : "text-[#D4A373]"}`} strokeWidth={1.25} />
    </div>
    <div className="font-heading text-3xl font-light">{value}</div>
    <p className={`text-xs uppercase tracking-[0.2em] mt-2 ${dark ? "text-stone-400" : "text-stone-500"}`}>{label}</p>
    {sub && <p className={`text-xs mt-1 ${dark ? "text-stone-500" : "text-stone-400"}`}>{sub}</p>}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [tab, setTab] = useState("users");
  const [showAdd, setShowAdd] = useState(false);
  const [newOwner, setNewOwner] = useState({ email: "", password: "", name: "" });

  async function createOwner(e) {
    e.preventDefault();
    try {
      await api.post("/admin/create-owner", newOwner);
      toast.success(t("admin.ownerCreated"));
      setShowAdd(false); setNewOwner({ email: "", password: "", name: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  }

  const load = async () => {
    try {
      const [s, u, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/users${q || filterStatus ? `?q=${encodeURIComponent(q)}&status=${filterStatus}` : ""}`),
        api.get("/admin/payments?limit=200"),
      ]);
      setStats(s.data); setUsers(u.data); setPayments(p.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [q, filterStatus]);

  if (user && user.role !== "owner") return <Navigate to="/" replace />;
  if (!stats) return <div className="text-stone-500 py-16 text-center">{t("common.loading")}</div>;

  async function extend(u, days) {
    if (!window.confirm(t("admin.confirmExtend", { name: u.email, days }))) return;
    try { await api.post(`/admin/users/${u.id}/extend`, { days }); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function grantLifetime(u) {
    if (!window.confirm(t("admin.confirmLifetime", { name: u.email }))) return;
    await api.post(`/admin/users/${u.id}/grant-lifetime`); toast.success(t("common.saved")); load();
  }
  async function revokeLifetime(u) {
    if (!window.confirm(t("admin.confirmRevoke", { name: u.email }))) return;
    try { await api.post(`/admin/users/${u.id}/revoke-lifetime`); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function delUser(u) {
    if (!window.confirm(t("admin.confirmDelete", { name: u.email }))) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(apiError(e)); }
  }

  const statusBadge = (s) => {
    const map = {
      lifetime: "bg-[#1C1917] text-white",
      active: "bg-[#8FA08B] text-white",
      trial: "bg-[#D4A373] text-stone-900",
      cancelled: "bg-stone-400 text-white",
      expired: "bg-red-100 text-red-700",
    };
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${map[s] || "bg-stone-200 text-stone-700"}`}>{t(`subPortal.status_${s}`)}</span>;
  };

  return (
    <div className="space-y-10 fade-in-up" data-testid="admin-dashboard">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("admin.eyebrow")}</p>
        <h1 className="font-heading text-5xl font-light">{t("admin.title")}</h1>
        <p className="text-stone-500 mt-2">{t("admin.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="MRR" value={`€${stats.mrr}`} sub={`ARR ≈ €${stats.arr}`} icon={TrendingUp} dark testId="kpi-mrr" />
        <KPI label={t("admin.totalRevenue")} value={`€${stats.revenue_total}`} sub={`€${stats.revenue_30d} · 30d`} icon={DollarSign} testId="kpi-revenue" />
        <KPI label={t("admin.usersActive")} value={stats.users_active} sub={`+${stats.users_lifetime} lifetime`} icon={Users} testId="kpi-active" />
        <KPI label={t("admin.usersTrial")} value={stats.users_trial} sub={`${stats.trial_expiring_soon} ≤ 3d`} icon={Clock} testId="kpi-trial" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1">{t("admin.newSignups")}</p>
          <div className="font-heading text-2xl font-light">{stats.new_signups_30d}</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1">{t("admin.revenueMonth")}</p>
          <div className="font-heading text-2xl font-light">€{stats.revenue_month}</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1">{t("admin.usersExpired")}</p>
          <div className="font-heading text-2xl font-light">{stats.users_expired}</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-1">{t("admin.usersCancelled")}</p>
          <div className="font-heading text-2xl font-light">{stats.users_cancelled}</div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <h3 className="font-heading text-2xl font-light mb-4">{t("admin.chartTitle")}</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={stats.monthly_revenue}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#78716C" }} />
            <YAxis tick={{ fontSize: 11, fill: "#78716C" }} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4" }} formatter={(v) => `€${v}`} />
            <Line type="monotone" dataKey="revenue" stroke="#D4A373" strokeWidth={2.5} dot={{ fill: "#B07D4F" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="border-b border-stone-200 flex gap-1">
        {["users", "payments"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px transition-all ${
              tab === k ? "border-[#D4A373] text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"
            }`}>
            {t(`admin.tab_${k}`)}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-3 bg-white border border-stone-200 rounded-full px-5 py-2.5 flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
              <input placeholder={t("admin.searchUsers")} value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="admin-user-search"
                className="flex-1 bg-transparent outline-none text-sm" />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="admin-filter-status"
              className="px-4 py-2.5 bg-white border border-stone-200 rounded-full text-sm">
              <option value="">{t("admin.allStatuses")}</option>
              <option value="active">{t("subPortal.status_active")}</option>
              <option value="trial">{t("subPortal.status_trial")}</option>
              <option value="expired">{t("subPortal.status_expired")}</option>
              <option value="cancelled">{t("subPortal.status_cancelled")}</option>
              <option value="lifetime">{t("subPortal.status_lifetime")}</option>
            </select>
            <button onClick={() => setShowAdd(!showAdd)} data-testid="admin-new-owner-btn"
              className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1C1917] text-white hover:bg-stone-800 text-sm">
              <Plus className="w-4 h-4" /> {t("admin.addAdmin")}
            </button>
          </div>

          {showAdd && (
            <form onSubmit={createOwner} data-testid="admin-new-owner-form"
              className="bg-white border border-stone-200 rounded-2xl p-6 grid md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("admin.newOwnerName")}</label>
                <input value={newOwner.name} onChange={(e) => setNewOwner({ ...newOwner, name: e.target.value })} data-testid="new-owner-name"
                  className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.email")} *</label>
                <input type="email" required value={newOwner.email} onChange={(e) => setNewOwner({ ...newOwner, email: e.target.value })} data-testid="new-owner-email"
                  className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("login.password")} *</label>
                <input type="password" required value={newOwner.password} onChange={(e) => setNewOwner({ ...newOwner, password: e.target.value })} data-testid="new-owner-password"
                  className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl text-sm" />
              </div>
              <div className="flex gap-2">
                <button type="submit" data-testid="submit-new-owner"
                  className="flex-1 px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800">{t("common.save")}</button>
                <button type="button" onClick={() => setShowAdd(false)}
                  className="px-4 py-2.5 rounded-full border border-stone-200 text-sm">{t("common.cancel")}</button>
              </div>
            </form>
          )}

          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                  <th className="text-left px-6 py-3">{t("admin.colUser")}</th>
                  <th className="text-left px-6 py-3">{t("admin.colStatus")}</th>
                  <th className="text-left px-6 py-3 hidden lg:table-cell">{t("admin.colUntil")}</th>
                  <th className="text-right px-6 py-3 hidden md:table-cell">{t("admin.colPaid")}</th>
                  <th className="text-right px-6 py-3 hidden md:table-cell">{t("admin.colData")}</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const until = u.subscription?.paid_until || u.subscription?.trial_ends_at;
                  return (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-b border-stone-50 hover:bg-stone-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-stone-900 flex items-center gap-2">
                          {u.role === "owner" && <Star className="w-3.5 h-3.5 text-[#D4A373]" fill="#D4A373" />}
                          {u.name || "—"}
                        </div>
                        <div className="text-xs text-stone-500">{u.email}</div>
                      </td>
                      <td className="px-6 py-4">{statusBadge(u.subscription?.status)}</td>
                      <td className="px-6 py-4 text-stone-600 text-xs hidden lg:table-cell">{until ? new Date(until).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                      <td className="px-6 py-4 text-right hidden md:table-cell">€{u.total_paid?.toFixed(2) || "0.00"}</td>
                      <td className="px-6 py-4 text-right text-xs text-stone-500 hidden md:table-cell">{u.patient_count}p · {u.appointment_count}a</td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {u.role !== "owner" && (
                          <div className="inline-flex gap-1">
                            <button onClick={() => extend(u, 30)} data-testid={`extend-30-${u.id}`} title="+30 días"
                              className="text-xs px-2 py-1 rounded-full border border-stone-200 hover:bg-stone-100">+30d</button>
                            <button onClick={() => extend(u, 365)} data-testid={`extend-365-${u.id}`} title="+1 año"
                              className="text-xs px-2 py-1 rounded-full border border-stone-200 hover:bg-stone-100">+1y</button>
                            {u.sub_status_raw !== "lifetime" ? (
                              <button onClick={() => grantLifetime(u)} data-testid={`grant-lifetime-${u.id}`} title={t("admin.grantLifetime")}
                                className="text-xs px-2 py-1 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white">
                                <Sparkles className="w-3 h-3" />
                              </button>
                            ) : (
                              <button onClick={() => revokeLifetime(u)} data-testid={`revoke-lifetime-${u.id}`} title={t("admin.revokeLifetime")}
                                className="text-xs px-2 py-1 rounded-full border border-stone-200 hover:bg-stone-100">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                            <button onClick={() => delUser(u)} data-testid={`del-user-${u.id}`}
                              className="text-xs px-2 py-1 rounded-full text-red-500 hover:bg-red-50">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && <div className="py-16 text-center text-stone-500 text-sm">{t("admin.noUsers")}</div>}
          </div>
        </div>
      )}

      {tab === "payments" && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                <th className="text-left px-6 py-3">{t("common.date")}</th>
                <th className="text-left px-6 py-3">{t("admin.colUser")}</th>
                <th className="text-left px-6 py-3 hidden md:table-cell">Plan</th>
                <th className="text-right px-6 py-3">{t("subPortal.colAmount")}</th>
                <th className="text-center px-6 py-3">{t("subPortal.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} data-testid={`admin-pay-${p.id}`} className="border-b border-stone-50">
                  <td className="px-6 py-4 text-stone-600 text-xs">{new Date(p.created_at).toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-6 py-4">
                    <div className="font-medium">{p.user_name || "—"}</div>
                    <div className="text-xs text-stone-500">{p.user_email}</div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">{p.plan}</td>
                  <td className="px-6 py-4 text-right font-medium">€{Number(p.amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      p.payment_status === "paid" ? "bg-[#8FA08B] text-white" :
                      p.payment_status === "initiated" ? "bg-stone-200 text-stone-700" : "bg-red-100 text-red-700"
                    }`}>{p.payment_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments.length === 0 && <div className="py-16 text-center text-stone-500 text-sm">{t("admin.noPayments")}</div>}
        </div>
      )}
    </div>
  );
}
